<template>
  <div class="fu-fpop-root">
    <div class="fu-fpop__scrim" :class="{ 'fu-fpop__scrim--tint': place.sheet }" data-test="fu-filter-scrim" @click="$emit('close')"></div>
    <div
      ref="panel"
      class="fu-fpop"
      :class="[`fu-fpop--${kind}`, { 'fu-fpop--sheet': place.sheet }]"
      :style="panelStyle"
      role="dialog"
      :aria-label="title"
      data-test="fu-filter-popover"
      :data-kind="kind"
    >
      <!-- Date range -->
      <div v-if="kind === 'date'" class="fu-fpop__body">
        <div class="fu-fpop__title">{{ title }}</div>
        <div class="fu-fpop__presets" role="group" aria-label="Date presets">
          <button
            v-for="p in presetList"
            :key="p.label"
            type="button"
            class="fu-fpop__preset"
            :class="{ 'fu-fpop__preset--on': p.from === draftFrom && p.to === draftTo }"
            :aria-pressed="p.from === draftFrom && p.to === draftTo ? 'true' : 'false'"
            data-test="fu-filter-preset"
            @click="pickPreset(p)"
          >{{ p.label }}</button>
        </div>
        <div class="fu-fpop__dates">
          <label class="fu-fpop__field">
            From
            <input
              type="date"
              class="fu-fpop__input"
              :value="draftFrom"
              :aria-invalid="dateError ? 'true' : 'false'"
              data-test="fu-filter-from"
              @input="draftFrom = $event.target.value"
              @change="draftFrom = $event.target.value"
            />
          </label>
          <label class="fu-fpop__field">
            To
            <input
              type="date"
              class="fu-fpop__input"
              :value="draftTo"
              :aria-invalid="dateError ? 'true' : 'false'"
              data-test="fu-filter-to"
              @input="draftTo = $event.target.value"
              @change="draftTo = $event.target.value"
            />
          </label>
        </div>
        <div v-if="dateError" class="fu-fpop__error" role="alert" data-test="fu-filter-error">{{ dateError }}</div>
        <div class="fu-fpop__actions">
          <button type="button" class="fu-fpop__btn fu-fpop__btn--ghost" @click="$emit('close')">Cancel</button>
          <button
            type="button"
            class="fu-fpop__btn fu-fpop__btn--primary"
            :disabled="!!dateError"
            data-test="fu-filter-apply"
            @click="applyDates"
          >Apply</button>
        </div>
      </div>

      <!-- Providers (or any required multi-pick) -->
      <div v-else-if="kind === 'providers'" class="fu-fpop__body">
        <div class="fu-fpop__title fu-fpop__title--gap">{{ title }}</div>
        <div class="fu-fpop__options">
          <label v-for="o in options" :key="String(o.value)" class="fu-fpop__option" data-test="fu-filter-option">
            <input
              type="checkbox"
              class="fu-fpop__check"
              :checked="isPicked(o.value)"
              @change="togglePick(o.value)"
            />
            <span class="fu-fpop__option-label">{{ o.label }}</span>
          </label>
          <div v-if="!options.length" class="fu-fpop__empty">{{ emptyText }}</div>
        </div>
        <div v-if="pickError" class="fu-fpop__error" role="alert" data-test="fu-filter-error">{{ pickError }}</div>
        <div class="fu-fpop__actions">
          <button type="button" class="fu-fpop__btn fu-fpop__btn--ghost" @click="$emit('close')">Cancel</button>
          <button
            type="button"
            class="fu-fpop__btn fu-fpop__btn--primary"
            :disabled="!!pickError"
            data-test="fu-filter-apply"
            @click="applyPicks"
          >Apply</button>
        </div>
      </div>

      <!-- "Your lists" -->
      <template v-else-if="kind === 'lists'">
        <div class="fu-fpop__lists-head">
          <span class="fu-fpop__title">{{ title }}</span>
          <span class="fu-fpop__lists-count">{{ lists.length }} {{ lists.length === 1 ? 'list' : 'lists' }}</span>
        </div>
        <div class="fu-fpop__lists">
          <button
            v-for="l in lists"
            :key="String(l.id)"
            type="button"
            class="fu-fpop__list"
            :class="{ 'fu-fpop__list--current': l.current }"
            :aria-current="l.current ? 'true' : null"
            data-test="fu-list-item"
            @click="$emit('select', l)"
          >
            <span class="fu-fpop__list-icon" aria-hidden="true">
              <fu-icon :name="l.current ? 'check' : 'list'" :size="14" />
            </span>
            <span class="fu-fpop__list-text">
              <span class="fu-fpop__list-name">{{ l.name }}</span>
              <span v-if="l.meta" class="fu-fpop__list-meta">{{ l.meta }}</span>
            </span>
          </button>
          <div v-if="!lists.length" class="fu-fpop__empty fu-fpop__empty--lists">{{ emptyText }}</div>
        </div>
        <div v-if="listsFoot === 'list'" class="fu-fpop__lists-foot" data-test="fu-lists-foot">
          Pick a list to open it. Lists are made on the follow-up page: select patients there and choose <strong>Create new list</strong>.
        </div>
        <div v-else class="fu-fpop__lists-foot" data-test="fu-lists-foot">
          To make a new list, select patients in the table and choose <strong>Create new list</strong>.
        </div>
      </template>
    </div>
  </div>
</template>

<script>
/**
 * The filter popovers on a follow-ups page (the export's lines 623–699): one positioned surface
 * with three bodies, picked by `kind`:
 *
 *   date       presets ("Last 3 / 6 / 12 / 24 months") + From / To native date inputs. Apply is
 *              disabled while pageChrome.js dateRangeError() has a message (end before start, more
 *              than `maxMonths` — the old page's 24-month toast). Emits apply({ from, to }) as
 *              'YYYY-MM-DD'.
 *   providers  a checkbox list; at least `minPicks` must stay ticked (the old page's "Please select
 *              one or more Providers"). Emits apply([values]).
 *   lists      the page's saved lists, newest first (the caller sorts), each { id, name, meta,
 *              current }. A click emits select(list). Creating a list stays in the table's bulk bar.
 *              `listsFoot: 'list'` on a saved-list page swaps the footer's hint (lists are made on
 *              the follow-up page, not here).
 *
 * Every body edits a DRAFT; nothing reaches the page until Apply. Escape, a click outside, Cancel —
 * or, on desktop, a scroll / resize that would leave it detached from its button — emit close.
 *
 * Placement: pageChrome.js placePopover() — under (or above) the button, clamped inside the
 * viewport; below 640px a bottom sheet over a dimmed page, with its buttons on the LEFT (the app's
 * floating agent button, z 9999, owns the bottom-right corner). z between the app header (1030)
 * and Bootstrap modals (1040).
 */
import FuIcon from './FuIcon.vue';
import { placePopover, dateRangePresets, dateRangeError } from '@/utils/followups/pageChrome.js';

export default {
  name: 'FollowupsFilterPopover',
  components: { FuIcon },
  props: {
    kind: {
      type: String,
      required: true,
      validator: v => ['date', 'providers', 'lists'].indexOf(v) !== -1,
    },
    title: { type: String, default: '' },
    anchor: { type: Object, default: null },        // the button's DOMRect (plain object)
    width: { type: Number, default: 330 },
    estHeight: { type: Number, default: 330 },
    align: { type: String, default: 'left' },
    emptyText: { type: String, default: 'Nothing to show.' },
    // date
    from: { type: String, default: '' },
    to: { type: String, default: '' },
    today: { type: String, default: '' },
    presetMonths: { type: Array, default: () => [3, 6, 12, 24] },
    maxMonths: { type: Number, default: 24 },
    // providers
    options: { type: Array, default: () => [] },     // [{ value, label }]
    selected: { type: Array, default: () => [] },
    minPicks: { type: Number, default: 1 },
    minPicksText: { type: String, default: 'Pick at least one provider.' },
    // lists
    lists: { type: Array, default: () => [] },       // [{ id, name, meta, current }]
    // The lists footer's hint: 'page' (a follow-up page — select rows here, Create new list) or
    // 'list' (a saved-list page, whose bulk Create new list is a "to be implemented" toast).
    listsFoot: { type: String, default: 'page', validator: v => ['page', 'list'].indexOf(v) !== -1 },
  },

  data() {
    return {
      draftFrom: this.from,
      draftTo: this.to,
      picks: this.selected.slice(),
      viewport: { width: window.innerWidth || 1280, height: window.innerHeight || 800 },
    };
  },

  computed: {
    place() {
      return placePopover(this.anchor, { width: this.width, height: this.estHeight, align: this.align }, this.viewport);
    },
    panelStyle() {
      const p = this.place;
      if (p.sheet) return {};
      const s = { left: `${p.left}px`, width: `${p.width}px`, maxHeight: `${p.maxHeight}px` };
      if (p.top !== undefined) s.top = `${p.top}px`;
      else s.bottom = `${p.bottom}px`;
      return s;
    },
    presetList() {
      return this.today ? dateRangePresets(this.today, this.presetMonths) : [];
    },
    dateError() {
      return this.kind === 'date' ? dateRangeError(this.draftFrom, this.draftTo, { maxMonths: this.maxMonths }) : '';
    },
    pickError() {
      return this.kind === 'providers' && this.picks.length < this.minPicks ? this.minPicksText : '';
    },
  },

  mounted() {
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onScroll, true);
    document.addEventListener('keydown', this.onKey, true);
    this.$nextTick(() => {
      const panel = this.$refs.panel;
      const first = panel && panel.querySelector('button, input, select, textarea');
      if (first && typeof first.focus === 'function') first.focus({ preventScroll: true });
    });
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll, true);
    document.removeEventListener('keydown', this.onKey, true);
  },

  methods: {
    onResize() {
      if (this.place.sheet) this.viewport = { width: window.innerWidth || 1280, height: window.innerHeight || 800 };
      else this.$emit('close');
    },
    onScroll(e) {
      const panel = this.$refs.panel;
      if (panel && e && e.target && e.target.nodeType === 1 && panel.contains(e.target)) return;
      if (!this.place.sheet) this.$emit('close');
    },
    onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.stopPropagation();
        this.$emit('close');
      }
    },
    pickPreset(p) {
      this.draftFrom = p.from;
      this.draftTo = p.to;
    },
    applyDates() {
      if (this.dateError) return;
      this.$emit('apply', { from: this.draftFrom, to: this.draftTo });
    },
    isPicked(v) {
      return this.picks.some(x => String(x) === String(v));
    },
    togglePick(v) {
      const i = this.picks.findIndex(x => String(x) === String(v));
      if (i === -1) this.picks.push(v);
      else this.picks.splice(i, 1);
    },
    applyPicks() {
      if (this.pickError) return;
      // In the options' own order, whatever order they were ticked in.
      const picked = this.options.filter(o => this.isPicked(o.value)).map(o => o.value);
      this.$emit('apply', picked);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-fpop__scrim {
  @include fu-scrim(false);
}

.fu-fpop__scrim--tint {
  background: rgba(12, 15, 14, 0.32);
}

.fu-fpop {
  @include fu-popover-surface;
  @include fu-font-base;
  position: fixed;
  z-index: $fu-z-popover;
  max-width: calc(100vw - 24px);
  animation: fu-fpop-in 0.16s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes fu-fpop-in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
}

.fu-fpop--sheet {
  left: 8px;
  right: 8px;
  bottom: 8px;
  width: auto;
  max-width: none;
  max-height: calc(100% - 16px);

  .fu-fpop__actions { justify-content: flex-start; }
  .fu-fpop__input { font-size: 16px; }
}

.fu-fpop__body {
  padding: 14px $fu-space-4;
}

.fu-fpop__title {
  @include fu-font-display;
  font-weight: 800;
  font-size: 15px;
}

.fu-fpop__title--gap {
  margin-bottom: $fu-space-3;
}

.fu-fpop__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 10px 0 $fu-space-3;
}

.fu-fpop__preset {
  @include fu-button-reset;
  @include fu-focus-ring;
  padding: 5px 10px;
  border-radius: $fu-radius-sm + 2px;
  font-size: 12.5px;
  font-weight: $fu-fw-semibold;
  border: 1px solid $fu-border;
  color: $fu-neutral-600;
  &:hover { background: $fu-neutral-50; }
}

.fu-fpop__preset--on {
  border-color: $fu-brand;
  background: $fu-brand-50;
  color: $fu-brand-700;
  &:hover { background: $fu-brand-50; }
}

.fu-fpop__dates {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
}

.fu-fpop__field {
  display: block;
  margin: 0;
  font-size: 12px;
  font-weight: $fu-fw-bold;
  color: $fu-neutral-600;
}

.fu-fpop__input {
  @include fu-input(40px);
  display: block;
  margin-top: 5px;
  padding: 0 10px;
  font-size: 13.5px;
}

.fu-fpop__error {
  margin-top: $fu-space-2;
  font-size: 12.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-danger-text;
}

.fu-fpop__options {
  display: flex;
  flex-direction: column;
  gap: 11px;
  max-height: 260px;
  overflow: auto;
}

.fu-fpop__option {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 13.5px;
  font-weight: $fu-fw-medium;
  cursor: pointer;
}

.fu-fpop__check {
  @include fu-checkbox;
}

.fu-fpop__option-label {
  min-width: 0;
  @include fu-ellipsis;
}

.fu-fpop__empty {
  font-size: 13px;
  color: $fu-text-muted;
}

.fu-fpop__empty--lists {
  padding: 10px;
}

.fu-fpop__actions {
  display: flex;
  justify-content: flex-end;
  gap: $fu-space-2;
  margin-top: 14px;
}

.fu-fpop__btn--primary { @include fu-btn(primary, sm); }
.fu-fpop__btn--ghost { @include fu-btn(ghost, sm); }

.fu-fpop__lists-head {
  display: flex;
  align-items: baseline;
  padding: 14px $fu-space-4 10px;
  border-bottom: 1px solid $fu-border;
}

.fu-fpop__lists-count {
  margin-left: auto;
  font-size: 12px;
  color: $fu-text-subtle;
}

.fu-fpop__lists {
  padding: 6px;
  max-height: 320px;
  overflow: auto;
}

.fu-fpop__list {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  border-radius: $fu-radius-sm + 3px;
  &:hover { background: $fu-neutral-50; }
}

.fu-fpop__list--current {
  background: $fu-brand-50;
  &:hover { background: $fu-brand-50; }
  .fu-fpop__list-icon { background: $fu-brand; color: #ffffff; }
  .fu-fpop__list-name { font-weight: $fu-fw-bold; }
}

.fu-fpop__list-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: $fu-radius-sm + 2px;
  background: $fu-brand-50;
  color: $fu-brand;
}

.fu-fpop__list-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.fu-fpop__list-name {
  @include fu-ellipsis;
  font-size: 13.5px;
  font-weight: $fu-fw-semibold;
}

.fu-fpop__list-meta {
  @include fu-ellipsis;
  font-size: 12px;
  color: $fu-text-subtle;
}

.fu-fpop__lists-foot {
  padding: 10px $fu-space-4 13px;
  border-top: 1px solid $fu-border;
  font-size: 12px;
  line-height: 1.45;
  color: $fu-text-muted;
  strong { color: $fu-neutral-700; }
}
</style>
